import api from "@/lib/api";
import { isEchoConnected } from "@/lib/echo-connection";
import { getEcho } from "@/lib/echo";
import type { JobCategory } from "@/lib/services/bookings";
import { notificationFallbackSync } from "@/lib/services/notification-fallback-sync";
import { usePendingOffersStore } from "@/stores/use-pending-offers-store";
import type Echo from "laravel-echo";
import * as Haptics from "expo-haptics";
import { router } from "expo-router";

export interface NewBookingOfferPayload {
  type: string;
  offer_id: number;
  booking_id: number;
  vehicle_id?: number | null;
  expires_at?: string;
  from_address?: string;
  to_address?: string;
  service_type?: string;
  scheduled_at?: string;
  priority?: boolean;
  job_category?: JobCategory;
  job_category_label?: string;
}

interface GeneralLocationPayload {
  latitude: number;
  longitude: number;
  heading: number | null;
  speed: number;
}

class DriverDispatchChannelService {
  private echoRef: Echo<"reverb"> | null = null;
  private channelName: string | null = null;
  private driverId: number | null = null;

  connect(driverId: number): void {
    this.driverId = driverId;
    this.echoRef = getEcho();

    if (!this.echoRef) {
      return;
    }

    if (this.channelName) {
      this.echoRef.leave(this.channelName);
    }

    this.channelName = `drivers.${driverId}`;

    this.echoRef
      .private(this.channelName)
      .listen(".NewBookingOffer", (data: NewBookingOfferPayload) => {
        this.handleNewOffer(data);
      })
      .listen(".OfferExpired", (data: { offer_id: number }) => {
        usePendingOffersStore.getState().removeOffer(data.offer_id);
      })
      .listen(
        ".QuoteAccepted",
        (data: { type: string; quote_id: number; booking_id: number }) => {
          console.log("Quote accepted", data);
          if (data.type === "quote_accepted" && data.quote_id && data.booking_id) {
            usePendingOffersStore.getState().removeOffersForBooking(data.booking_id);
            router.replace(`/bookings/${data.booking_id}/live-driving`);
          }
        },
      )
      .listen(
        ".QuoteCountered",
        (data: {
          type: string;
          quote_id: number;
          booking_id: number;
          vehicle_id?: number;
        }) => {
          if (
            data.type === "quote_countered" &&
            data.quote_id &&
            data.booking_id
          ) {
            router.push(`/bookings/${data.booking_id}/quotes`);
          }
        },
      )
      .listen(
        ".QuoteRejected",
        (data: { type: string; quote_id: number; booking_id: number }) => {
          console.log("Quote rejected", data);
          if (data.type === "quote_rejected" && data.quote_id && data.booking_id) {
            router.push(`/bookings/${data.booking_id}/quotes`);
          }
        },
      );
  }

  disconnect(): void {
    if (this.echoRef && this.channelName) {
      this.echoRef.leave(this.channelName);
    }
    this.channelName = null;
    this.driverId = null;
    this.echoRef = null;
  }

  /**
   * Send general (non-booking) location over the drivers.{id} channel.
   * Returns false when Echo is unavailable so callers can fall back to HTTP.
   */
  sendGeneralLocation(location: GeneralLocationPayload): boolean {
    if (
      !this.echoRef ||
      !this.channelName ||
      !this.driverId ||
      !isEchoConnected(this.echoRef)
    ) {
      return false;
    }

    try {
      this.echoRef.connector.pusher.send_event(
        "SendLocation",
        {
          user_id: this.driverId,
          latitude: location.latitude,
          longitude: location.longitude,
          heading: location.heading,
          speed: location.speed,
        },
        this.channelName,
      );
      return true;
    } catch (error) {
      console.warn("Failed to send general location via WebSocket", error);
      return false;
    }
  }

  private async handleNewOffer(data: NewBookingOfferPayload): Promise<void> {

    usePendingOffersStore.getState().addOffer({
      offer_id: data.offer_id,
      booking_id: data.booking_id,
      vehicle_id: data.vehicle_id,
      from_address: data.from_address,
      to_address: data.to_address,
      service_type: data.service_type,
      scheduled_at: data.scheduled_at,
      expires_at: data.expires_at,
      priority: data.priority,
      job_category: data.job_category,
      job_category_label: data.job_category_label,
    });

    try {
      await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
    } catch {
      // Haptics unavailable on some devices
    }

    try {
      await api.post(`/booking-offers/${data.offer_id}/view`);
    } catch (error) {
      console.warn("Failed to ack offer view", error);
    }
  }

  async syncPendingOffers(): Promise<void> {
    if (!this.driverId) {
      return;
    }

    await notificationFallbackSync.syncDriverOffers();
  }

}

export const driverDispatchChannel = new DriverDispatchChannelService();
